import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { format } from "date-fns";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DISCIPLINES = [
  { id: "football", label: "Football", sub: "On-pitch technical & game IQ" },
  { id: "fitness", label: "Fitness", sub: "Strength & conditioning" },
];

const PLANS = [
  { id: "single", label: "Single Session", sub: "£15 · one-off" },
  { id: "monthly", label: "Monthly", sub: "£60 / month · flat" },
];

const isSaturday = (d) => d.getDay() === 6;

export default function Booking() {
  const [plan, setPlan] = useState("single");
  const [discipline, setDiscipline] = useState("football");
  const [date, setDate] = useState(undefined);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [time, setTime] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [maxDate, setMaxDate] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings`).then((r) => {
      const m = r.data.booking_window_months || 2;
      const d = new Date();
      d.setMonth(d.getMonth() + m);
      setMaxDate(d);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!date) return;
    setTime(null);
    setLoadingSlots(true);
    const ds = format(date, "yyyy-MM-dd");
    axios
      .get(`${API}/availability`, { params: { date: ds } })
      .then((r) => setSlots(r.data.slots))
      .catch(() => toast.error("Could not load times"))
      .finally(() => setLoadingSlots(false));
  }, [date]);

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const canBook = discipline && date && time && name.trim() && validEmail;

  const handleReserve = async () => {
    if (!canBook) {
      toast.error("Complete all fields to reserve");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/bookings/checkout`, {
        name: name.trim(),
        email: email.trim(),
        session_type: discipline,
        plan,
        date: format(date, "yyyy-MM-dd"),
        time,
        notes,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      if (e.response?.status === 409) {
        toast.error(e.response.data?.detail || "That slot has just been booked. Please choose another time.");
        // refresh slots so the taken time now shows as Booked
        const ds = format(date, "yyyy-MM-dd");
        axios.get(`${API}/availability`, { params: { date: ds } })
          .then((r) => setSlots(r.data.slots)).catch(() => {});
        setTime(null);
      } else {
        toast.error("Something went wrong. Try again.");
      }
      setSubmitting(false);
    }
  };

  return (
    <section id="book" data-testid="booking" className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-40">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="mb-14 md:mb-20"
      >
        <span className="text-[#2E6BE6] text-xs uppercase tracking-[0.3em]">Reserve</span>
        <h2 className="font-display font-bold uppercase tracking-tighter text-4xl md:text-7xl mt-4 leading-[0.9]">
          Book your slot
        </h2>
        <p className="text-white/50 mt-4 max-w-md font-body">
          60-minute private session · Weekdays 9am–7pm & Sundays 2pm–7pm (last start 6pm) · Saturdays closed · £15 single or £60/mo.
        </p>
      </motion.div>

      <div className="grid lg:grid-cols-12 gap-px bg-white/10 border border-white/10">
        {/* Left: discipline + calendar + slots */}
        <div className="lg:col-span-8 bg-black/40 backdrop-blur-md p-6 md:p-10 space-y-10">
          {/* Plan */}
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-4">01 / Plan</p>
            <div className="grid sm:grid-cols-2 gap-3" data-testid="plan-select">
              {PLANS.map((p) => (
                <button
                  key={p.id}
                  data-testid={`plan-${p.id}`}
                  onClick={() => setPlan(p.id)}
                  className={`text-left p-5 border transition-all ${
                    plan === p.id
                      ? "border-[#D4AF37] bg-[#D4AF37]/5"
                      : "border-white/15 hover:border-white/40"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-display font-bold uppercase text-2xl">{p.label}</span>
                    {plan === p.id && <Check className="w-5 h-5 text-[#D4AF37]" />}
                  </div>
                  <span className="text-white/50 text-sm">{p.sub}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Discipline */}
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-4">02 / Discipline</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {DISCIPLINES.map((d) => (
                <button
                  key={d.id}
                  data-testid={`discipline-${d.id}`}
                  onClick={() => setDiscipline(d.id)}
                  className={`text-left p-5 border transition-all ${
                    discipline === d.id
                      ? "border-[#D4AF37] bg-[#D4AF37]/5"
                      : "border-white/15 hover:border-white/40"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-display font-bold uppercase text-2xl">{d.label}</span>
                    {discipline === d.id && <Check className="w-5 h-5 text-[#D4AF37]" />}
                  </div>
                  <span className="text-white/50 text-sm">{d.sub}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Calendar */}
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-4">03 / Date</p>
            <div className="border border-white/15 p-4 inline-block" data-testid="calendar">
              <DayPicker
                mode="single"
                selected={date}
                onSelect={setDate}
                disabled={[{ before: new Date() }, isSaturday, ...(maxDate ? [{ after: maxDate }] : [])]}
                weekStartsOn={1}
                styles={{
                  caption: { color: "#fff" },
                  head_cell: { color: "rgba(255,255,255,0.4)", textTransform: "uppercase", fontSize: 11 },
                  day: { color: "#fff", borderRadius: 0 },
                  nav_button: { color: "#D4AF37" },
                }}
                modifiersStyles={{
                  selected: { background: "#D4AF37", color: "#000", borderRadius: 0 },
                  disabled: { color: "rgba(255,255,255,0.2)" },
                }}
              />
            </div>
          </div>

          {/* Time slots */}
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-4">04 / Time</p>
            {!date && <p className="text-white/40 text-sm">Select a date to see available times.</p>}
            {date && loadingSlots && (
              <div className="flex items-center gap-2 text-white/50 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading times…
              </div>
            )}
            {date && !loadingSlots && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="time-slots">
                {slots.map((s) => (
                  <button
                    key={s.time}
                    data-testid={`slot-${s.time}`}
                    disabled={!s.available}
                    onClick={() => setTime(s.time)}
                    className={`py-3 text-sm border transition-all flex flex-col items-center justify-center leading-tight ${
                      time === s.time
                        ? "bg-[#D4AF37] text-black border-[#D4AF37] scale-[1.03]"
                        : s.available
                        ? "border-white/15 text-white hover:border-white/50"
                        : "border-white/5 bg-white/[0.02] text-white/25 cursor-not-allowed"
                    }`}
                  >
                    <span className={!s.available ? "line-through" : ""}>{s.time}</span>
                    {!s.available && (
                      <span className="text-[9px] uppercase tracking-[0.15em] text-white/55 mt-0.5">Booked</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: details + summary */}
        <div className="lg:col-span-4 bg-black/55 backdrop-blur-md p-6 md:p-10 flex flex-col">
          <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-6">05 / Your Details</p>
          <div className="space-y-5 flex-1">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-white/40">Full name</label>
              <input
                data-testid="input-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jordan Smith"
                className="w-full bg-transparent border-b border-white/20 py-2 mt-2 outline-none focus:border-[#D4AF37] transition-colors placeholder:text-white/25"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-white/40">Email</label>
              <input
                data-testid="input-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="w-full bg-transparent border-b border-white/20 py-2 mt-2 outline-none focus:border-[#D4AF37] transition-colors placeholder:text-white/25"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-white/40">Notes / goals (optional)</label>
              <textarea
                data-testid="input-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="What do you want to work on?"
                className="w-full bg-transparent border-b border-white/20 py-2 mt-2 outline-none focus:border-[#D4AF37] transition-colors resize-none placeholder:text-white/25"
              />
            </div>
          </div>

          {/* Summary */}
          <div className="mt-8 border-t border-white/10 pt-6 space-y-2 text-sm" data-testid="booking-summary">
            <Row label="Plan" value={plan === "monthly" ? "MONTHLY" : "SINGLE"} />
            <Row label="Discipline" value={discipline ? discipline.toUpperCase() : "—"} />
            <Row label="Date" value={date ? format(date, "EEE, dd MMM yyyy") : "—"} />
            <Row label="Time" value={time || "—"} />
            <div className="flex justify-between pt-3 mt-2 border-t border-white/10">
              <span className="text-white/60 uppercase tracking-wide">Total</span>
              <span className="font-display font-bold text-2xl text-[#D4AF37]">{plan === "monthly" ? "£60/mo" : "£15.00"}</span>
            </div>
          </div>

          <button
            data-testid="reserve-pay-btn"
            onClick={handleReserve}
            disabled={!canBook || submitting}
            className={`mt-6 w-full py-4 text-sm uppercase tracking-[0.15em] font-semibold flex items-center justify-center gap-2 transition-all ${
              canBook && !submitting
                ? "bg-[#D4AF37] text-black hover:-translate-y-0.5"
                : "bg-white/10 text-white/40 cursor-not-allowed"
            }`}
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Redirecting…
              </>
            ) : (
              <>Reserve &amp; Pay →</>
            )}
          </button>
          <p className="text-white/30 text-xs mt-3 text-center">Secure checkout via Stripe.</p>
          <p className="text-white/30 text-xs mt-2 text-center">Free cancellation or reschedule up to 24h before — full refund. Prices include VAT.</p>
        </div>
      </div>
    </section>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between">
    <span className="text-white/40 uppercase tracking-wide">{label}</span>
    <span className="text-white">{value}</span>
  </div>
);
